import { Disc3, LibraryBig } from "lucide-react";
import { useMemo } from "react";

import { Badge, EmptyState, PageHeader, Skeleton, StatusMessage, Tooltip } from "@ceedero/ui";

import { useCloudLibrary } from "@/cloud/hooks/use-cloud-pull";
import { AppPage } from "@/components/app-page";
import { useTranslation } from "@/localization/hooks/use-translation";
import { selectLiveProjects } from "@/music-catalog/utils/catalog-view";
import { ShelfBoard } from "@/shelf/components/shelf-board/shelf-board";
import {
  countShelvedDiscs,
  indexProjectsById,
  selectLiveShelves,
} from "@/shelf/utils/shelf-board-view";

/**
 * The Shelves this account has arranged its library on.
 *
 * A view of its own beside the Music catalog rather than a mode inside it,
 * because it answers a different question: the catalog is a list of what
 * exists, and a Shelf is where the reader put it.
 *
 * The board is the page: once there are racks to draw, it fills everything
 * under the header, as the desktop's board fills its view, and the two
 * headline counts ride in the header rather than taking a band of the board's
 * height.
 *
 * **This application reads Shelves and writes none of them.** Every Shelf is
 * built, filled, moved and removed in the desktop; the browser draws the board
 * it is handed. That is the same rule every other Cloud Library interface is
 * held to and not a limitation of the board — so there is nothing here to add a
 * rack with, and no control that is drawn dead waiting for one.
 */
export const ShelfPage = () => {
  const { t } = useTranslation();
  const library = useCloudLibrary();

  const shelves = useMemo(
    () => (library.data ? selectLiveShelves(library.data.shelves) : []),
    [library.data],
  );
  // Resolved against the live projects, so a disc naming one the account has
  // since removed from the cloud reads as absent rather than as itself.
  const projectsById = useMemo(
    () => (library.data ? indexProjectsById(selectLiveProjects(library.data.projects)) : new Map()),
    [library.data],
  );
  const discCount = useMemo(() => countShelvedDiscs(shelves), [shelves]);
  const hasBoard = library.data !== undefined && shelves.length > 0;

  const header = (
    <PageHeader
      title={t("shelf.title")}
      description={t("shelf.description")}
      actions={
        hasBoard ? (
          <div className="flex items-center gap-4">
            <Tooltip content={t("shelf.rackCount", { count: shelves.length })} side="bottom">
              <Badge
                variant="inline"
                aria-label={t("shelf.rackCount", { count: shelves.length })}
                className="text-sm"
              >
                <LibraryBig />
                {shelves.length}
              </Badge>
            </Tooltip>
            <Tooltip content={t("shelf.discCount", { count: discCount })} side="bottom">
              <Badge
                variant="inline"
                aria-label={t("shelf.discCount", { count: discCount })}
                className="text-sm"
              >
                <Disc3 />
                {discCount}
              </Badge>
            </Tooltip>
          </div>
        ) : undefined
      }
    />
  );

  if (hasBoard) {
    return (
      <AppPage fills header={header}>
        <ShelfBoard projectsById={projectsById} shelves={shelves} />
      </AppPage>
    );
  }

  return (
    <AppPage width="full" header={header}>
      {library.isPending && <Skeleton className="h-96 w-full" />}

      {/*
        A failed read must read as "could not load" and never as "you have
        nothing": these are the reader's own Shelves, and an empty board would
        say they had lost them.
      */}
      {library.isError && <StatusMessage tone="error">{t("shelf.unavailable")}</StatusMessage>}

      {/*
        The one kind of nothing this page has: an account that has never built
        a rack. There is no search here to match none of, and no filter to find
        none — and the sentence has to say where a Shelf comes from, because it
        cannot be made here.
      */}
      {library.data && shelves.length === 0 && (
        <EmptyState
          icon={<LibraryBig />}
          title={t("shelf.empty.title")}
          description={t("shelf.empty.description")}
        />
      )}
    </AppPage>
  );
};
